import { useEffect, useState } from 'react';
import { fontMono } from './utils';

// Animated radial score ring. `score` is 0..110 or null.
export const ScoreRing = ({
  score,
  size = 190,
  stroke = 15,
  color = '#8B1E3F',
  track = '#F3EFEA',
  threshold = null,
  suffix = '/100',
  label = null,
  testId = 'score-ring',
}) => {
  const [display, setDisplay] = useState(0);
  const target = score == null ? 0 : Number(score);

  useEffect(() => {
    let raf;
    const start = performance.now();
    const dur = 950;
    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(target * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(display / 100, 1));
  const offset = circ * (1 - pct);
  const center = size / 2;

  // threshold tick angle (starts at top, clockwise)
  let tick = null;
  if (threshold != null) {
    const a = (threshold / 100) * 2 * Math.PI - Math.PI / 2;
    tick = {
      x1: center + (r - stroke / 2) * Math.cos(a),
      y1: center + (r - stroke / 2) * Math.sin(a),
      x2: center + (r + stroke / 2) * Math.cos(a),
      y2: center + (r + stroke / 2) * Math.sin(a),
    };
  }

  return (
    <div className="relative inline-flex items-center justify-center" data-testid={testId}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={center} cy={center} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.1s linear' }}
        />
        {tick && (
          <line
            x1={tick.x1}
            y1={tick.y1}
            x2={tick.x2}
            y2={tick.y2}
            stroke="#1A1A1E"
            strokeWidth={2.5}
            strokeLinecap="round"
          />
        )}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="flex items-baseline" style={{ fontFamily: fontMono }}>
          <span
            className="font-bold tabular-nums leading-none"
            style={{ fontSize: size * 0.26, color: '#1A1A1E' }}
            data-testid={`${testId}-value`}
          >
            {score == null ? '—' : Math.round(display)}
          </span>
          {score != null && (
            <span className="text-sm font-medium ml-0.5" style={{ color: '#8C8C9A' }}>
              {suffix}
            </span>
          )}
        </div>
        {label && (
          <span className="text-[11px] uppercase tracking-widest font-semibold mt-1" style={{ color: '#8C8C9A' }}>
            {label}
          </span>
        )}
      </div>
    </div>
  );
};

// Compact mini ring for board cards
export const MiniRing = ({ score, size = 68, stroke = 7, color = '#8B1E3F', track = '#F3EFEA', testId }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = score == null ? 0 : Math.max(0, Math.min(score / 100, 1));
  const offset = circ * (1 - pct);
  const center = size / 2;
  return (
    <div className="relative inline-flex items-center justify-center" data-testid={testId}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={center} cy={center} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={center}
          cy={center}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1)' }}
        />
      </svg>
      <span
        className="absolute font-bold tabular-nums"
        style={{ fontFamily: fontMono, fontSize: size * 0.28, color: '#1A1A1E' }}
      >
        {score == null ? '—' : Math.round(score)}
      </span>
    </div>
  );
};
